/**
 * The light field — the handbook cover's light burst, rendered live.
 *
 * One full-screen triangle and one fragment shader, in raw WebGL: no three.js
 * on the home page (the handbook page's 3D bundle is ~517 KB; this is ~4 KB).
 *
 * What the shader draws, all modelled on the cover art:
 *   - a white-gold point of light with a tight core and wide halo
 *   - gold streaks fanning out to the left, teal pillars rising upward
 *   - light flowing OUTWARD along the rays, so the field is never still
 *   - an anamorphic flare: the horizontal streak through the point on the cover
 *   - a faint floor of light below the point, flowing toward the viewer
 *   - an expanding ring when the headline's "YOU!" lands (uFlash)
 *
 * Angular structure is sampled on the unit circle (the ray DIRECTION), never on
 * atan() — atan() wraps at ±π and would draw a hard seam straight through the
 * rays, which point left, exactly where the wrap is.
 *
 * Rendered below device resolution and upscaled by CSS: the field is soft, so
 * it costs a fraction of full resolution for no visible loss.
 */

const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;

uniform vec2  uRes;      // drawing-buffer size, px
uniform float uTime;     // seconds
uniform vec2  uLight;    // light position, 0..1, y up
uniform float uIgnite;   // 0..1 entrance
uniform float uFlash;    // 1 -> 0 pulse when "YOU!" lands
uniform float uScroll;   // 0..1 how far the hero has scrolled away
uniform float uOctaves;  // noise detail, set by device tier

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) {
    if (float(i) >= uOctaves) break;
    v += a * noise(p);
    p = p * 2.03 + 17.1;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv  = gl_FragCoord.xy / uRes;
  float asp = uRes.x / uRes.y;
  vec2 p = vec2(uv.x * asp, uv.y);
  vec2 L = vec2(uLight.x * asp, uLight.y);
  vec2 d = p - L;
  float r = length(d);
  vec2 dn = d / max(r, 1e-4);          // ray direction on the unit circle
  float t = uTime;

  vec3 navy   = vec3(0.043, 0.071, 0.125);
  vec3 deep   = vec3(0.090, 0.212, 0.365);
  vec3 teal   = vec3(0.098, 0.722, 0.651);
  vec3 gold   = vec3(0.965, 0.769, 0.325);
  vec3 warm   = vec3(1.000, 0.925, 0.780);
  vec3 indigo = vec3(0.294, 0.247, 0.569);

  // ground: navy, lifting toward deep navy in the upper left
  vec3 col = mix(navy, deep, 0.55 * smoothstep(1.3, 0.0, length(uv - vec2(0.0, 1.0))));
  col += indigo * 0.16 * smoothstep(1.1, 0.0, length(uv - vec2(1.0, 0.0)));

  // --- rays -----------------------------------------------------------------
  float ig = uIgnite;
  float reach = mix(0.12, 1.75, ig) * (1.0 - 0.45 * uScroll) * (1.0 + 0.35 * uFlash);

  // where the cover sends its light: gold fanning left, teal pillars upward
  float leftFan = smoothstep(-0.35, 1.0, dot(dn, normalize(vec2(-1.0, 0.28))));
  float upward  = smoothstep(0.15, 1.0, dn.y);
  float weight  = 0.18 + leftFan + 0.85 * upward;

  float broad = fbm(dn * 7.0  + vec2(t * 0.030, -t * 0.022));
  float fine  = noise(dn * 46.0 + vec2(-t * 0.018, t * 0.012));
  float rays  = pow(broad, 3.0) * 2.2 + pow(fine, 7.0) * 2.6;

  // light travelling outward along each ray
  float flow = noise(dn * 30.0 + vec2(r * 5.5 - t * 0.85, 0.0));
  rays *= 0.62 + 0.75 * flow;

  float fall = pow(max(0.0, 1.0 - r / reach), 1.7);
  vec3 rayCol = mix(gold, teal, clamp(upward * 1.25, 0.0, 1.0));
  col += rayCol * rays * weight * fall * 0.36 * ig;

  // --- the point of light ------------------------------------------------------
  float pulse = 1.0 + 0.06 * sin(t * 1.3);
  float core = exp(-r * r * 150.0) * 1.3 + exp(-r * 7.5) * 0.32 + exp(-r * 2.1) * 0.10;
  col += warm * core * pulse * (ig + 1.6 * uFlash);

  // anamorphic flare: the horizontal streak through the point on the cover
  float flare = exp(-abs(d.y) * 110.0) * exp(-abs(d.x) * 2.6);
  col += gold * flare * 0.32 * ig * (1.0 + uFlash);

  // --- a floor of light below the point, flowing toward the viewer -------------
  if (d.y < -0.004) {
    float z  = 1.0 / (-d.y * 4.0 + 0.02);      // depth
    float fx = d.x * z;                           // floor coordinates
    float fz = z * 0.55 + t * 0.55;               // moving toward the viewer
    float lines = pow(1.0 - abs(fract(fx * 1.6) - 0.5) * 2.0, 26.0)
                + pow(1.0 - abs(fract(fz)       - 0.5) * 2.0, 34.0) * 0.8;
    float fade = exp(-z * 0.16) * smoothstep(0.0, 0.22, -d.y) * exp(-abs(d.x) * 1.3);
    col += mix(teal, gold, 0.3) * lines * fade * 0.14 * ig;
  }

  // --- the "YOU!" flash: a ring of light running outward ------------------------
  float ringR = (1.0 - uFlash) * 1.35;
  col += warm * exp(-pow((r - ringR) * 16.0, 2.0)) * uFlash * 0.40;

  // keep the copy side calm: the headline and the stair live on the left
  col *= mix(0.52, 1.0, smoothstep(0.05, 0.72, uv.x));
  col *= 1.0 - 0.38 * uScroll;

  // vignette, filmic roll-off, grain (the grain also kills gradient banding)
  col *= smoothstep(1.45, 0.30, length((uv - 0.5) * vec2(1.05, 1.25)));
  // Exposure. Was 1.35; the light read too bright behind Ava & Artie in review
  // (2026-09-10), so the whole field was brought down together — rays, core,
  // halo, flare and floor — rather than just shrinking the core.
  col = 1.0 - exp(-col * 1.12);
  col += (hash(gl_FragCoord.xy + fract(t * 7.0) * 97.0) - 0.5) * 0.028;

  gl_FragColor = vec4(col, 1.0);
}
`;

export class LightField {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{scale?:number, octaves?:number, maxDpr?:number, onLost?:()=>void}} opts
   */
  constructor(canvas, { scale = 0.65, octaves = 4, maxDpr = 1.5, onLost } = {}) {
    this.canvas = canvas;
    this.scale = scale;
    this.octaves = octaves;
    this.maxDpr = maxDpr;
    this.onLost = onLost;

    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'low-power',
    });
    if (!gl) throw new Error('WebGL unavailable');
    this.gl = gl;

    const program = gl.createProgram();
    for (const [type, src] of [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, FRAG]]) {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) || 'shader compile failed');
      gl.attachShader(program, sh);
    }
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'link failed');
    gl.useProgram(program);

    // One triangle that covers the screen; cheaper than a two-triangle quad.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.u = {};
    for (const name of ['uRes', 'uTime', 'uLight', 'uIgnite', 'uFlash', 'uScroll', 'uOctaves']) {
      this.u[name] = gl.getUniformLocation(program, name);
    }

    this._lost = (e) => {
      e.preventDefault();
      this.lost = true;
      this.onLost?.();
    };
    canvas.addEventListener('webglcontextlost', this._lost);
    this.resize();
  }

  /** Construct or return null — callers fall back to the CSS burst. */
  static create(canvas, opts) {
    try {
      return new LightField(canvas, opts);
    } catch (err) {
      console.warn('[home] light field unavailable; keeping the CSS burst.', err);
      return null;
    }
  }

  setQuality({ scale, octaves }) {
    if (scale) this.scale = scale;
    if (octaves) this.octaves = octaves;
    this.resize();
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, this.maxDpr);
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr * this.scale));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr * this.scale));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.gl.viewport(0, 0, w, h);
  }

  /** @param {{time:number, light:[number,number], ignite:number, flash:number, scroll:number}} s */
  render(s) {
    if (this.lost) return;
    const { gl, u } = this;
    gl.uniform2f(u.uRes, this.canvas.width, this.canvas.height);
    gl.uniform1f(u.uTime, s.time);
    gl.uniform2f(u.uLight, s.light[0], s.light[1]);
    gl.uniform1f(u.uIgnite, s.ignite);
    gl.uniform1f(u.uFlash, s.flash);
    gl.uniform1f(u.uScroll, s.scroll);
    gl.uniform1f(u.uOctaves, this.octaves);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  dispose() {
    this.canvas.removeEventListener('webglcontextlost', this._lost);
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
